import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnInit,
  QueryList,
  ViewChildren,
} from "@angular/core";
import { CommonModule, DatePipe } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { CdkTextareaAutosize, TextFieldModule } from "@angular/cdk/text-field";
import swal from "sweetalert2";

import { MonitoreoIAService } from "../monitoreo-ia.service";
import { InventarioIA } from "src/app/shared/model/iso42001/inventario-ia.model";
import { MatrizEvidencia } from "src/app/shared/model/iso42001/matriz-riesgo.model";
import {
  ConclusionMonitoreo,
  MonitoreoIA,
  MonitoreoIADetalle,
} from "src/app/shared/model/iso42001/monitoreo-ia.model";
import { MatrizRiesgoService } from "src/app/matriz-riesgos-ia/matriz-riesgo.service";

/** Una opción de la lista de conclusiones. */
interface OpcionConclusion {
  valor: ConclusionMonitoreo;
  etiqueta: string;
}

@Component({
  selector: "app-monitoreo-inventario-ia",
  standalone: true,
  templateUrl: "./monitoreo-inventario-ia.component.html",
  styleUrls: ["./monitoreo-inventario-ia.component.css"],
  imports: [
    CommonModule,
    DatePipe,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    TextFieldModule,
  ],
})
export class MonitoreoInventarioIAComponent implements OnInit {
  /* Textareas de observaciones: cdkTextareaAutosize solo recalcula su alto
     ante escritura del usuario, no cuando el texto se carga por código (caso
     de "Ver"). Se fuerza el recálculo con reajustarObservaciones(). */
  @ViewChildren(CdkTextareaAutosize)
  private autosizes!: QueryList<CdkTextareaAutosize>;

  idInventarioIa = 0;
  inventario: InventarioIA = {};
  monitoreos: MonitoreoIA[] = [];

  /** Evaluación en captura. Sus aspectos se sirven desde el backend. */
  evaluacion: MonitoreoIA = {};
  formVisible = false;
  esEdicion = false;
  /** Consulta sin edición: el formulario se muestra bloqueado (botón Ver). */
  soloLectura = false;

  /**
   * Fecha ligada al datepicker. Es un Date aparte y no evaluacion.fechaEvaluacion
   * (que es texto ISO) porque el DateAdapter de Material interpreta la cadena
   * "2026-07-15" como medianoche UTC y en México la mostraría un día antes. Se
   * convierte en la frontera, siempre con getters locales.
   */
  fechaPicker: Date | null = null;

  /** Tope del calendario: no se puede evaluar el futuro. */
  readonly hoy = new Date();

  /** Paginación client-side del histórico. */
  tamPagina = 5;
  paginaActual = 1;

  loading = false;
  guardando = false;
  error = "";

  /** Plantilla en blanco que envía el backend; se clona en cada alta. */
  private plantilla: MonitoreoIADetalle[] = [];

  readonly conclusiones: OpcionConclusion[] = [
    { valor: "SIN_CAMBIOS", etiqueta: "La IA continúa operando sin cambios." },
    { valor: "MEJORA", etiqueta: "La IA requiere acciones de mejora." },
    { valor: "REEVALUAR_RIESGOS", etiqueta: "La IA requiere reevaluación de riesgos." },
    // "La IA debe regresar a la etapa de Desarrollo y Entrenamiento" salió de la
    // lista el 18/09/2026. Se comprobó antes que no la usara ninguna evaluación
    // guardada: las 10 de QA y producción son SIN_CAMBIOS.
    { valor: "RETIRO", etiqueta: "Se recomienda su retiro controlado." },
  ];

  /**
   * Textos del dictamen. El backend manda la clave; la pantalla solo la traduce
   * para no duplicar la regla de cálculo en dos lugares.
   */
  private static readonly DICTAMENES: {
    [clave: string]: { titulo: string; detalle: string; clase: string };
  } = {
    FAVORABLE: {
      titulo: "Favorable",
      detalle: "El sistema cumple con todos los criterios evaluados.",
      clase: "is-favorable",
    },
    FAVORABLE_REVISION: {
      titulo: "Favorable con revisión",
      detalle: "Se detectaron desviaciones puntuales que requieren atención.",
      clase: "is-observaciones",
    },
    FAVORABLE_REEVALUAR: {
      titulo: "Favorable con reevaluación de riesgos",
      detalle:
        "Aparecieron riesgos o impactos nuevos: hay que volver a valorarlos.",
      clase: "is-observaciones",
    },
    NO_FAVORABLE_RETIRO: {
      titulo: "No favorable",
      detalle: "La IA dejó de aportar valor: se recomienda análisis para retiro.",
      clase: "is-no-favorable",
    },
    // Ya no se producen. Se dejan para que el historial siga sabiendo leer las
    // evaluaciones guardadas antes del 17/09/2026, cuando el dictamen salía del
    // número de hallazgos y no de cuáles. Sin esto, abrir una vieja reventaría
    // al buscar su texto.
    FAVORABLE_OBS: {
      titulo: "Favorable con observaciones",
      detalle: "Se detectaron desviaciones puntuales que requieren atención.",
      clase: "is-observaciones",
    },
    NO_FAVORABLE: {
      titulo: "No favorable",
      detalle: "El sistema requiere revisión: los hallazgos son significativos.",
      clase: "is-no-favorable",
    },
  };

  /**
   * Los dos aspectos que el formato de criterios señala por su nombre. Se
   * comparan por TEXTO, igual que en MonitoreoIABuss: el formato de RH trae
   * cinco criterios y el nuestro seis, así que su "opción 5" es nuestro aspecto
   * 6 y amarrarlo al número miraría el aspecto equivocado.
   */
  private static readonly ASPECTO_APORTA_VALOR =
    "La IA sigue aportando valor al proceso";
  private static readonly ASPECTO_RIESGOS_NUEVOS =
    "No se identificaron riesgos o impactos nuevos relevantes";

  /**
   * Orden del aspecto cuya observación está expandida, o null. Uno a la vez.
   *
   * El textarea crece solo hasta 16 filas y de ahí en adelante hay que rodar
   * dentro de una caja chica, que para releer una observación larga es
   * incómodo. Expandir la abre a pantalla de lectura sin sacarla de su sitio, y
   * al salir del campo vuelve sola a su tamaño: no hay que acordarse de cerrar.
   */
  ordenExpandido: number | null = null;

  /** A partir de aquí la observación ya no se abarca de un vistazo. */
  private static readonly LARGO_PARA_EXPANDIR = 120;

  esLarga(asp: MonitoreoIADetalle): boolean {
    return (
      (asp.observaciones || "").length >
      MonitoreoInventarioIAComponent.LARGO_PARA_EXPANDIR
    );
  }

  alternaExpandir(orden: number): void {
    this.ordenExpandido = this.ordenExpandido === orden ? null : orden;
  }

  constructor(
    // El sistema de evidencias es compartido: el mismo que usan los
    // activos y los riesgos de la matriz. Aqui solo cambia el
    // tipoOrigen, que dice de que cuelga cada archivo.
    private readonly evidenciaServ: MatrizRiesgoService,
    private service: MonitoreoIAService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get("id");
    if (!id) {
      this.regresar();
      return;
    }
    this.idInventarioIa = Number(id);
    this.cargar();
  }

  /**
   * @param reiniciarPagina true tras un alta: la evaluación nueva se ordena
   * primero (fecha DESC) y el usuario tiene que verla. false al recargar tras
   * editar o eliminar, para no sacarlo de la página donde estaba trabajando.
   */
  cargar(reiniciarPagina = false): void {
    this.loading = true;
    this.error = "";
    this.service.consultar(this.idInventarioIa).subscribe({
      next: (res) => {
        if (res.mensajeError) {
          this.error = res.mensajeError;
        } else {
          this.inventario = res.inventario || {};
          this.monitoreos = res.monitoreos || [];
          this.plantilla = res.plantillaAspectos || [];
        }
        if (reiniciarPagina) {
          this.paginaActual = 1;
        }
        // La página no solo se pinta acotada: se persiste acotada. Sin esto un
        // valor fuera de rango sobrevive a la recarga (al borrar la última fila
        // de la página 2 la vista se ve bien, pero paginaActual sigue en 2) y
        // la pantalla salta sola en cuanto la lista vuelve a crecer.
        this.paginaActual = this.paginaNorm();
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.error = "No se pudo cargar el monitoreo. Intenta de nuevo.";
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  // ------------------------------------------------------------------
  // Captura
  // ------------------------------------------------------------------

  nuevaEvaluacion(): void {
    if (!this.plantilla.length) {
      this.error = "No se pudo cargar el formato de evaluación. Recarga la pantalla.";
      return;
    }
    this.esEdicion = false;
    this.soloLectura = false;
    this.error = "";
    this.fechaPicker = new Date();
    this.evaluacion = {
      idInventarioIa: this.idInventarioIa,
      conclusion: null,
      // Clon profundo: sin él, capturar una evaluación contaminaría la
      // plantilla y la siguiente alta arrancaría con datos de la anterior.
      aspectos: this.plantilla.map((a) => ({
        orden: a.orden,
        aspecto: a.aspecto,
        estado: null,
        observaciones: "",
      })),
    };
    this.formVisible = true;
    // Nada subido todavia, y la cola en blanco: si no se limpiara, los
    // archivos de una captura abandonada se colarian en la siguiente.
    this.evidenciasPorAspecto = new Map<number, MatrizEvidencia[]>();
    this.pendientesPorOrden = new Map<number, File[]>();
    this.ordenAbierto = null;
    this.cdr.detectChanges();
    this.scrollArriba();
  }

  editar(item: MonitoreoIA): void {
    this.esEdicion = true;
    this.soloLectura = false;
    this.error = "";
    this.fechaPicker = this.isoADate(item.fechaEvaluacion);
    // Copia propia: sin ella, escribir en el formulario modificaría la fila del
    // histórico en vivo y "Cancelar" no revertiría nada.
    this.evaluacion = {
      ...item,
      aspectos: (item.aspectos || []).map((a) => ({ ...a })),
    };
    this.formVisible = true;
    // Los archivos de los seis aspectos. Va aqui y no en ver() porque
    // ver() llama a editar(): asi queda cubierto el camino de consulta
    // y el de edicion con una sola llamada.
    this.cargarEvidencias();
    this.pendientesPorOrden = new Map<number, File[]>();
    this.ordenAbierto = null;
    this.cdr.detectChanges();
    this.reajustarObservaciones();
    this.scrollArriba();
  }

  /**
   * Consulta de una evaluación sin editarla. Disponible siempre —en especial
   * para las cerradas por la ventana de 24h—, porque el detalle (aspectos y
   * observaciones) es la evidencia que se muestra en una auditoría.
   */
  ver(item: MonitoreoIA): void {
    this.editar(item);
    this.soloLectura = true;
    this.cdr.detectChanges();
    this.reajustarObservaciones();
  }

  /**
   * Fuerza a cada textarea de observaciones a recalcular su altura para que
   * muestre TODO el texto. Necesario en "Ver" y "Editar": el contenido se
   * asigna por código y cdkTextareaAutosize no reajusta solo en ese caso.
   * El setTimeout deja que Angular pinte los textareas antes de medir.
   */
  private reajustarObservaciones(): void {
    setTimeout(() => {
      this.autosizes?.forEach((a) => a.resizeToFitContent(true));
    });
  }

  cancelarCaptura(): void {
    this.formVisible = false;
    this.esEdicion = false;
    this.soloLectura = false;
    this.evaluacion = {};
    this.fechaPicker = null;
    this.error = "";
    this.pendientesPorOrden = new Map<number, File[]>();
    this.ordenAbierto = null;
    this.cdr.detectChanges();
    this.scrollArriba();
  }

  /**
   * Devuelve la vista al inicio al abrir o cerrar la captura.
   *
   * Hace falta porque .main-content declara min-height: calc(100vh - 123px): la
   * página no se acorta al ocultar el formulario, así que el navegador conserva
   * la posición y el usuario se queda mirando el vacío.
   *
   * Se mueven .main-panel y la ventana: el layout scrollea el panel en
   * escritorio (PerfectScrollbar, admin-layout.component.ts) y la ventana en el
   * resto de los casos, y el componente no tiene por qué saber cuál está activo.
   */
  private scrollArriba(): void {
    document
      .querySelector(".main-panel")
      ?.scrollTo({ top: 0, behavior: "smooth" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  marcar(aspecto: MonitoreoIADetalle, estado: "CUMPLE" | "NO_CUMPLE"): void {
    if (this.soloLectura) {
      return;
    }
    // Volver a picar la misma opción la deselecciona: permite corregir sin
    // dejar la fila trabada en una respuesta equivocada.
    aspecto.estado = aspecto.estado === estado ? null : estado;
    // La observación se conserva en cualquier estado: en NO CUMPLE es
    // obligatoria (describe el hallazgo) y en CUMPLE es opcional, útil como
    // evidencia de eficacia del control ante una auditoría.
  }

  // ------------------------------------------------------------------
  // Resumen en vivo (solo indicativo: el dictamen oficial lo calcula el backend)
  // ------------------------------------------------------------------

  get aspectos(): MonitoreoIADetalle[] {
    return this.evaluacion.aspectos || [];
  }

  get totalCumple(): number {
    return this.aspectos.filter((a) => a.estado === "CUMPLE").length;
  }

  get totalNoCumple(): number {
    return this.aspectos.filter((a) => a.estado === "NO_CUMPLE").length;
  }

  get respondidos(): number {
    return this.totalCumple + this.totalNoCumple;
  }

  get completa(): boolean {
    return this.aspectos.length > 0 && this.respondidos === this.aspectos.length;
  }

  /** Misma escalera que MonitoreoIABuss.calculaDictamen, solo para previsualizar. */
  get dictamenPrevio(): string {
    if (!this.completa) {
      return "";
    }
    const falla = (texto: string) =>
      this.aspectos.some(
        (a) => a.aspecto === texto && a.estado === "NO_CUMPLE",
      );
    if (falla(MonitoreoInventarioIAComponent.ASPECTO_APORTA_VALOR)) {
      return "NO_FAVORABLE_RETIRO";
    }
    if (falla(MonitoreoInventarioIAComponent.ASPECTO_RIESGOS_NUEVOS)) {
      return "FAVORABLE_REEVALUAR";
    }
    if (this.totalNoCumple === 0) {
      return "FAVORABLE";
    }
    return "FAVORABLE_REVISION";
  }

  /**
   * Puntaje de la evaluación en curso, de 0 a 100, sobre los aspectos que ya se
   * marcaron Cumple. Lo que no se ha contestado cuenta como 0: la barra dice
   * "cómo va", no "cómo va a quedar".
   *
   * OJO: es ayuda visual, no la regla. El dictamen NO sale de este número —sale
   * de cuáles aspectos fallan, en MonitoreoIABuss.calculaDictamen—, y por eso
   * puede haber un 83.3 con dictamen No favorable: basta que el aspecto que
   * falle sea el de aportar valor.
   */
  get puntaje(): number {
    const total = this.aspectos.length;
    return total ? (this.totalCumple * 100) / total : 0;
  }

  get puntajeTexto(): string {
    return this.puntaje.toFixed(1);
  }

  /** El umbral del formato de criterios. */
  get bajoUmbral(): boolean {
    return this.puntaje < 80;
  }

  /**
   * Color de la barra: el del dictamen, no el del umbral.
   *
   * Aplica la MISMA escalera que calculaDictamen pero sobre lo que se lleva
   * marcado, sin exigir que la evaluación esté completa: lo que aún no se
   * contesta todavía no es un hallazgo. Así el color va cambiando respuesta a
   * respuesta y acaba justo en el del dictamen final.
   *
   * Que salga de aquí y no del umbral es a propósito: con el color por el 80 se
   * podía ver la barra en verde diciendo "80 o más" y el dictamen de abajo en
   * rojo, porque basta que el aspecto que falle sea el de aportar valor. Ahora
   * los dos dicen lo mismo.
   */
  get barraClase(): string {
    const falla = (texto: string) =>
      this.aspectos.some((a) => a.aspecto === texto && a.estado === "NO_CUMPLE");
    if (falla(MonitoreoInventarioIAComponent.ASPECTO_APORTA_VALOR)) {
      return "is-no-favorable";
    }
    if (falla(MonitoreoInventarioIAComponent.ASPECTO_RIESGOS_NUEVOS)) {
      return "is-observaciones";
    }
    return this.totalNoCumple === 0 ? "is-favorable" : "is-observaciones";
  }

  /**
   * La tabla de dictamen de la ventana de criterios, de mejor a peor, que es
   * el orden en que viene el formato y el que se lee natural. OJO: NO es el
   * orden en que se evalúa. MonitoreoIABuss.calculaDictamen va al revés —mira
   * primero si falla "aporta valor", luego "riesgos nuevos"— porque ahí gana la
   * primera que aplica y hay casos donde caben dos. Por eso los dos últimos
   * renglones se nombran por su aspecto y no por un "si además…": cada uno
   * manda sobre los de arriba.
   * Los textos y las clases de color salen de DICTAMENES, así que no pueden
   * decir una cosa distinta de lo que luego se ve en el resumen.
   */
  get criteriosDictamen(): {
    cuando: string;
    dictamen: string;
    clase: string;
  }[] {
    const textos = MonitoreoInventarioIAComponent.DICTAMENES;
    return [
      {
        clave: "FAVORABLE",
        cuando: "Cumplen todos los aspectos",
      },
      {
        clave: "FAVORABLE_REVISION",
        cuando: "Incumple alguno de los demás aspectos",
      },
      {
        clave: "FAVORABLE_REEVALUAR",
        cuando: `Incumple «${MonitoreoInventarioIAComponent.ASPECTO_RIESGOS_NUEVOS}»`,
      },
      {
        clave: "NO_FAVORABLE_RETIRO",
        cuando: `Incumple «${MonitoreoInventarioIAComponent.ASPECTO_APORTA_VALOR}»`,
      },
    ].map((fila) => ({
      cuando: fila.cuando,
      dictamen: textos[fila.clave].titulo,
      clase: textos[fila.clave].clase,
    }));
  }

  /**
   * Escape cierra la ventana de evidencia, que es lo que espera cualquiera
   * que se encuentre un diálogo encima. Sólo actúa si está abierta: si no,
   * deja pasar la tecla al resto.
   */
  @HostListener("document:keydown.escape")
  alPulsarEscape(): void {
    if (this.ordenAbierto !== null) {
      this.ordenAbierto = null;
    }
  }

  get dictamenTitulo(): string {
    if (!this.completa) {
      return "Evaluación en curso";
    }
    return this.textoDictamen(this.dictamenPrevio);
  }

  get dictamenDetalle(): string {
    if (!this.completa) {
      const faltan = this.aspectos.length - this.respondidos;
      return `Faltan ${faltan} aspecto${faltan === 1 ? "" : "s"} por evaluar.`;
    }
    return MonitoreoInventarioIAComponent.DICTAMENES[this.dictamenPrevio].detalle;
  }

  get dictamenClase(): string {
    if (!this.completa) {
      return "is-pendiente";
    }
    return MonitoreoInventarioIAComponent.DICTAMENES[this.dictamenPrevio].clase;
  }

  /**
   * La conclusión que corresponde al dictamen en curso, o null mientras falte
   * algún aspecto. Ya no se elige: cada dictamen tiene su conclusión, y el
   * formato de criterios las nombra igual —"con revisión" pide acciones de
   * mejora, "con reevaluación de riesgos" pide justo eso, y el no favorable
   * recomienda el retiro—. Es la MISMA tabla que MonitoreoIABuss.conclusionPara
   * aplica al guardar; aquí sólo se adelanta para pintarla.
   */
  get conclusionSugerida(): ConclusionMonitoreo | null {
    if (!this.completa) {
      return null;
    }
    const porDictamen: { [clave: string]: ConclusionMonitoreo } = {
      FAVORABLE: "SIN_CAMBIOS",
      FAVORABLE_REVISION: "MEJORA",
      FAVORABLE_REEVALUAR: "REEVALUAR_RIESGOS",
      NO_FAVORABLE_RETIRO: "RETIRO",
    };
    return porDictamen[this.dictamenPrevio] || null;
  }

  /**
   * La que se marca en pantalla. En consulta, la guardada (el backend la
   * derivó con la misma regla, así que coincide); en captura, la que va a
   * salir con lo que se lleva marcado.
   */
  get conclusionMarcada(): ConclusionMonitoreo | null {
    return this.soloLectura
      ? this.evaluacion.conclusion || null
      : this.conclusionSugerida;
  }

  /* ================= Evidencia por aspecto =================
   *
   * Lo que pide una auditoria no es "la evidencia del monitoreo" en
   * bloque, sino por que ESE aspecto salio favorable. Por eso los
   * archivos cuelgan del id del aspecto y no del de la evaluacion.
   */

  /** El tipo con el que el backend reconoce este origen. */
  readonly ORIGEN_ASPECTO = "MONITOREO_ASPECTO";

  /** Cuantos archivos tiene cada aspecto, por id de detalle. */
  evidenciasPorAspecto = new Map<number, MatrizEvidencia[]>();

  /**
   * El ORDEN del aspecto cuyo panel esta abierto. null si ninguno.
   *
   * La llave es el orden y no el id del detalle a proposito: en una
   * evaluacion que se esta capturando los renglones todavia no existen
   * en la base y no tienen id, pero el orden (1..6) esta desde el
   * primer momento. Con el id como llave no habia forma de abrir el
   * panel antes de guardar, que es justo lo que hacia falta.
   */
  ordenAbierto: number | null = null;
  subiendoEvidencia = false;

  /**
   * Archivos elegidos ANTES de que la evaluacion exista, por orden de
   * aspecto.
   *
   * No se pueden subir todavia —cuelgan del id del renglon, que nace al
   * guardar—, asi que esperan aqui y se mandan en cuanto la evaluacion
   * queda registrada. Es el mismo camino que usa Solicitar IA con sus
   * adjuntos.
   */
  pendientesPorOrden = new Map<number, File[]>();

  /** El aspecto abierto, sea nuevo o guardado. */
  get aspectoDelPanel(): MonitoreoIADetalle | null {
    if (this.ordenAbierto == null) {
      return null;
    }
    return this.aspectos.find((a) => a.orden === this.ordenAbierto) || null;
  }

  /** Los archivos YA SUBIDOS del aspecto abierto. */
  get evidenciasAbiertas(): MatrizEvidencia[] {
    const asp = this.aspectoDelPanel;
    const id = asp ? asp.idMonitoreoDetalle : null;
    return id == null ? [] : this.evidenciasPorAspecto.get(id) || [];
  }

  /** Los que esperan a que la evaluacion se registre. */
  get pendientesAbiertos(): File[] {
    return this.ordenAbierto == null
      ? []
      : this.pendientesPorOrden.get(this.ordenAbierto) || [];
  }

  /** Cuantos hay en total: los subidos mas los que esperan. */
  cuantasEvidencias(asp: MonitoreoIADetalle): number {
    const id = asp.idMonitoreoDetalle;
    const subidas = id == null ? 0 : (this.evidenciasPorAspecto.get(id) || []).length;
    const orden = asp.orden;
    const esperando = orden == null ? 0 : (this.pendientesPorOrden.get(orden) || []).length;
    return subidas + esperando;
  }

  /** Cuantos hay esperando en toda la evaluacion. */
  get totalPendientes(): number {
    let n = 0;
    this.pendientesPorOrden.forEach((fs) => (n += fs.length));
    return n;
  }

  /**
   * Carga los archivos de los seis aspectos.
   *
   * Se piden todos de golpe al abrir la evaluacion y no uno por uno al
   * pulsar el clip: son seis consultas chicas, y asi el contador ya
   * esta puesto cuando la pantalla se dibuja.
   */
  cargarEvidencias(): void {
    this.evidenciasPorAspecto = new Map<number, MatrizEvidencia[]>();
    for (const asp of this.aspectos) {
      const id = asp.idMonitoreoDetalle;
      if (id == null) {
        continue;
      }
      this.evidenciaServ.consultarEvidencias(this.ORIGEN_ASPECTO, id).subscribe({
        next: (res) => {
          this.evidenciasPorAspecto.set(id, res.evidencias || []);
          this.cdr.detectChanges();
        },
        error: () => {
          // Que no cargue el contador de un aspecto no puede tumbar la
          // pantalla: se deja en cero y los demas siguen.
          this.evidenciasPorAspecto.set(id, []);
        },
      });
    }
  }

  abrirEvidencias(asp: MonitoreoIADetalle): void {
    const orden = asp.orden;
    if (orden == null) {
      return;
    }
    this.ordenAbierto = this.ordenAbierto === orden ? null : orden;
  }

  archivoElegido(evt: Event): void {
    const input = evt.target as HTMLInputElement;
    const archivo = input.files && input.files.length ? input.files[0] : null;
    // Se limpia el input SIEMPRE: si no, elegir el mismo archivo dos
    // veces seguidas no dispara el evento y parece que no pasa nada.
    input.value = "";
    // El backend lo rechaza de todos modos, pero cortarlo aqui evita
    // mandar el archivo para nada y ensenar un error que ya sabiamos.
    const asp = this.aspectoDelPanel;
    if (!archivo || !asp || this.soloLectura) {
      return;
    }

    // La evaluacion todavia no existe: el archivo espera y se manda en
    // cuanto se registre. Antes aqui no habia nada que hacer y por eso
    // el clip decia "Al registrar".
    const id = asp.idMonitoreoDetalle;
    if (id == null) {
      const orden = asp.orden!;
      const cola = this.pendientesPorOrden.get(orden) || [];
      cola.push(archivo);
      this.pendientesPorOrden.set(orden, cola);
      this.cdr.detectChanges();
      return;
    }

    this.subiendoEvidencia = true;
    this.evidenciaServ.subirEvidencia(archivo, this.ORIGEN_ASPECTO, id).subscribe({
      next: (res) => {
        this.subiendoEvidencia = false;
        if (res.codigoError) {
          this.avisoEvidencia(res.mensajeError || "No se pudo subir la evidencia.");
          this.cdr.detectChanges();
          return;
        }
        this.recargaAspecto(id);
      },
      error: () => {
        this.subiendoEvidencia = false;
        this.avisoEvidencia("No se pudo subir la evidencia.");
        this.cdr.detectChanges();
      },
    });
  }

  /** Sacar de la cola uno que todavia no se ha subido. */
  quitarPendiente(i: number): void {
    if (this.ordenAbierto == null) {
      return;
    }
    const cola = this.pendientesPorOrden.get(this.ordenAbierto) || [];
    cola.splice(i, 1);
    if (cola.length) {
      this.pendientesPorOrden.set(this.ordenAbierto, cola);
    } else {
      this.pendientesPorOrden.delete(this.ordenAbierto);
    }
    this.cdr.detectChanges();
  }

  /** Verdadero si el navegador puede mostrarlo sin bajarlo. */
  sePuedeVer(ev: MatrizEvidencia): boolean {
    const m = (ev.mimeType || "").toLowerCase();
    return m === "application/pdf" || m.indexOf("image/") === 0;
  }

  /**
   * Abre el archivo en una pestana nueva, sin bajarlo.
   *
   * Es lo que hace falta para revisar: en una auditoria se abren muchos
   * respaldos y bajarlos todos al disco estorba mas que ayuda.
   */
  verEvidencia(ev: MatrizEvidencia): void {
    this.evidenciaServ.descargarEvidencia(ev.idEvidencia!).subscribe({
      next: (blob) => {
        // El blob llega sin tipo. Sin el MIME correcto el navegador lo
        // trata como descarga en vez de mostrarlo, que es justo lo que
        // este boton quiere evitar.
        const url = URL.createObjectURL(
          new Blob([blob], { type: ev.mimeType || "application/pdf" })
        );
        const tab = window.open(url, "_blank");
        if (!tab) {
          URL.revokeObjectURL(url);
          this.avisoEvidencia(
            "El navegador bloqueó la ventana. Permite las ventanas emergentes de este sitio para ver el archivo."
          );
          return;
        }
        // No se revoca de inmediato: la pestana nueva todavia lo esta
        // leyendo y se quedaria en blanco. Se suelta despues.
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      },
      error: () => this.avisoEvidencia("No se pudo abrir el archivo."),
    });
  }

  descargarEvidencia(ev: MatrizEvidencia): void {
    this.evidenciaServ.descargarEvidencia(ev.idEvidencia!).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = ev.nombreArchivo || "evidencia";
        a.click();
        URL.revokeObjectURL(url);
      },
      error: () => this.avisoEvidencia("No se pudo descargar el archivo."),
    });
  }

  quitarEvidencia(ev: MatrizEvidencia): void {
    if (this.soloLectura) {
      return;
    }
    const asp = this.aspectoDelPanel;
    const id = asp ? asp.idMonitoreoDetalle : null;
    swal
      .fire({
        icon: "warning",
        title: "¿Quitar esta evidencia?",
        html:
          `<b>${ev.nombreArchivo}</b><br>` +
          `<span style="color:#64748b;font-size:13px">El archivo se borra del ` +
          `servidor y no se puede recuperar.</span>`,
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: "Sí, quitar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#b71c1c",
      })
      .then((r) => {
        if (!r.isConfirmed) {
          return;
        }
        this.evidenciaServ.eliminarEvidencia(ev.idEvidencia!).subscribe({
          next: (res) => {
            if (res.codigoError) {
              this.avisoEvidencia(res.mensajeError || "No se pudo quitar.");
              return;
            }
            if (id != null) {
              this.recargaAspecto(id);
            }
          },
          error: () => this.avisoEvidencia("No se pudo quitar el archivo."),
        });
      });
  }

  private recargaAspecto(id: number): void {
    this.evidenciaServ.consultarEvidencias(this.ORIGEN_ASPECTO, id).subscribe({
      next: (res) => {
        this.evidenciasPorAspecto.set(id, res.evidencias || []);
        this.cdr.detectChanges();
      },
    });
  }

  /** Un solo lugar para los avisos de evidencia, con el estilo del resto
   *  de la pantalla. */
  private avisoEvidencia(texto: string): void {
    swal.fire({ icon: "error", title: "Evidencia", text: texto });
  }

  /** Los bytes en algo legible. */
  pesoLegible(bytes?: number): string {
    if (!bytes) {
      return "";
    }
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${Math.round(bytes / 1024)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /** Un hallazgo sin explicación no sirve como evidencia: se marca en rojo. */
  faltaObservacion(aspecto: MonitoreoIADetalle): boolean {
    return (
      aspecto.estado === "NO_CUMPLE" && !(aspecto.observaciones || "").trim()
    );
  }

  // ------------------------------------------------------------------
  // Guardado
  // ------------------------------------------------------------------

  guardar(): void {
    if (this.soloLectura) {
      return;
    }
    const problema = this.validar();
    if (problema) {
      this.error = problema;
      this.cdr.detectChanges();
      return;
    }

    // El Date del calendario se pasa a texto ISO justo antes de enviarlo, con
    // getters locales: es el formato que espera el backend.
    this.evaluacion.fechaEvaluacion = this.dateAISO(this.fechaPicker);
    // La conclusión viaja por completar el bean, pero el backend la vuelve a
    // derivar del dictamen e ignora ésta: no se puede guardar una que no cuadre.
    this.evaluacion.conclusion = this.conclusionSugerida;

    this.guardando = true;
    this.error = "";

    const peticion = this.esEdicion
      ? this.service.actualizar(this.evaluacion)
      : this.service.guardar(this.evaluacion);

    peticion.subscribe({
      next: (res) => {
        if (res.mensajeError) {
          this.guardando = false;
          this.error = res.mensajeError;
          this.cdr.detectChanges();
          return;
        }
        // Se captura antes de limpiar el formulario: cancelarCaptura() apaga
        // esEdicion y borra la cola, y las dos cosas hacen falta abajo.
        const fueEdicion = this.esEdicion;
        const cola = this.pendientesPorOrden;
        const idEval = res.idGenerado || this.evaluacion.idMonitoreoIa;

        // Los archivos que se eligieron durante la captura suben ahora,
        // que ya existen los renglones de los que cuelgan.
        if (cola.size && idEval != null) {
          this.subePendientes(idEval, cola, fueEdicion);
          return;
        }

        this.guardando = false;
        this.terminaGuardado(fueEdicion, 0, []);
      },
      error: () => {
        this.guardando = false;
        this.error = "No se pudo guardar la evaluación. Intenta de nuevo.";
        this.cdr.detectChanges();
      },
    });
  }

  /** Mientras van subiendo los archivos que esperaban. */
  subiendoPendientes = false;

  /**
   * Manda los archivos que esperaban a que la evaluación existiera.
   *
   * Hace falta un viaje más para saber a qué renglón va cada uno: el alta
   * devuelve el id de la EVALUACIÓN, no el de sus seis renglones, y es el
   * del renglón el que sostiene la evidencia. Se piden con obtener() y se
   * casan por ORDEN, que es lo único que la pantalla y la base comparten
   * antes de guardar.
   */
  private subePendientes(
    idEval: number,
    cola: Map<number, File[]>,
    fueEdicion: boolean,
  ): void {
    this.subiendoPendientes = true;
    this.cdr.detectChanges();

    this.service.obtener(idEval).subscribe({
      next: (res) => {
        const aspectos = (res.monitoreo && res.monitoreo.aspectos) || [];
        const idPorOrden = new Map<number, number>();
        for (const a of aspectos) {
          if (a.orden != null && a.idMonitoreoDetalle != null) {
            idPorOrden.set(a.orden, a.idMonitoreoDetalle);
          }
        }

        const tareas: Array<{ archivo: File; id: number }> = [];
        const huerfanos: string[] = [];
        cola.forEach((archivos, orden) => {
          const id = idPorOrden.get(orden);
          archivos.forEach((archivo) => {
            if (id == null) {
              // No deberia pasar: los seis renglones nacen con la
              // evaluacion. Si pasara, se dice cual se quedo fuera en
              // vez de perderlo en silencio.
              huerfanos.push(archivo.name);
            } else {
              tareas.push({ archivo, id });
            }
          });
        });

        this.subeUnaAUna(tareas, 0, 0, huerfanos, fueEdicion);
      },
      error: () => {
        // La evaluación SÍ se guardó; lo que falló fue releerla. No se
        // pierde nada más que los adjuntos, y se dice.
        const nombres: string[] = [];
        cola.forEach((archivos) => archivos.forEach((a) => nombres.push(a.name)));
        this.subiendoPendientes = false;
        this.guardando = false;
        this.terminaGuardado(fueEdicion, 0, nombres);
      },
    });
  }

  /**
   * Uno por uno y no todos a la vez: seis archivos en paralelo contra el
   * mismo almacén no van más rápido y enredan qué falló.
   */
  private subeUnaAUna(
    tareas: Array<{ archivo: File; id: number }>,
    i: number,
    subidos: number,
    fallidos: string[],
    fueEdicion: boolean,
  ): void {
    if (i >= tareas.length) {
      this.subiendoPendientes = false;
      this.guardando = false;
      this.terminaGuardado(fueEdicion, subidos, fallidos);
      return;
    }
    const t = tareas[i];
    this.evidenciaServ.subirEvidencia(t.archivo, this.ORIGEN_ASPECTO, t.id).subscribe({
      next: (r: any) => {
        if (r && r.codigoError) {
          fallidos.push(t.archivo.name);
        } else {
          subidos++;
        }
        this.subeUnaAUna(tareas, i + 1, subidos, fallidos, fueEdicion);
      },
      error: () => {
        fallidos.push(t.archivo.name);
        this.subeUnaAUna(tareas, i + 1, subidos, fallidos, fueEdicion);
      },
    });
  }

  /** Cerrar la captura y decir cómo fue. */
  private terminaGuardado(fueEdicion: boolean, subidos: number, fallidos: string[]): void {
    this.cancelarCaptura();
    // Solo el alta regresa a la página 1, que es donde cae la evaluación
    // recién creada. En la edición se respeta la página del usuario.
    this.cargar(!fueEdicion);

    const titulo = fueEdicion ? "Evaluación actualizada" : "Evaluación registrada";

    if (fallidos.length) {
      // La evaluación quedó bien; lo que falló fueron los archivos. Se
      // dice cuáles y que todavía se pueden adjuntar: la ventana de 24
      // horas acaba de empezar.
      swal.fire({
        icon: "warning",
        title: titulo,
        html:
          `Se guardó, pero ${fallidos.length} archivo(s) no se pudieron subir:` +
          `<br><b>${fallidos.join(", ")}</b><br>` +
          `<span style="color:#64748b;font-size:13px">Ábrela y adjúntalos: ` +
          `hay 24 horas para hacerlo.</span>`,
      });
      return;
    }

    swal.fire({
      icon: "success",
      title: titulo,
      text: subidos ? `Con ${subidos} archivo(s) de evidencia.` : undefined,
      timer: 1800,
      showConfirmButton: false,
    });
  }

  /**
   * Validación de cortesía: evita el viaje al servidor y señala el campo. La
   * validación que manda es la del backend (MonitoreoIABuss.validar).
   */
  private validar(): string {
    if (!this.fechaPicker) {
      return "Indica la fecha de evaluación.";
    }
    // En ISO el orden alfabético es el cronológico: comparar texto basta.
    if (this.dateAISO(this.fechaPicker) > this.hoyISO()) {
      return "La fecha de evaluación no puede ser posterior al día de hoy.";
    }
    if (!this.completa) {
      return "Responde los seis aspectos antes de guardar.";
    }
    const sinObs = this.aspectos.findIndex((a) => this.faltaObservacion(a));
    if (sinObs >= 0) {
      return `El aspecto ${sinObs + 1} no cumple: describe el hallazgo en observaciones.`;
    }
    return "";
  }

  eliminar(item: MonitoreoIA): void {
    swal
      .fire({
        title: "¿Eliminar esta evaluación?",
        html:
          `Se eliminará de forma permanente la evaluación <strong>${this.escapar(item.consecutivo)}</strong> ` +
          `del ${this.fechaLegible(item.fechaEvaluacion)} y sus seis aspectos.<br><br>` +
          `Esta acción no se puede deshacer y la evidencia se pierde.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#c62828",
        focusCancel: true,
      })
      .then((r) => {
        if (!r.isConfirmed) {
          return;
        }
        this.service.eliminar(item.idMonitoreoIa!).subscribe({
          next: (res) => {
            if (res.mensajeError) {
              swal.fire({
                icon: "error",
                title: "No se pudo eliminar",
                text: res.mensajeError,
              });
              return;
            }
            // Si se borró justo la evaluación abierta en el formulario, hay que
            // cerrarlo: dejarlo vivo con un id inexistente condena cada intento
            // de guardar a VAL-002 y la captura queda atrapada sin salida.
            if (
              this.formVisible &&
              this.esEdicion &&
              this.evaluacion.idMonitoreoIa === item.idMonitoreoIa
            ) {
              this.cancelarCaptura();
            }
            this.cargar();
            swal.fire({
              icon: "success",
              title: "Evaluación eliminada",
              timer: 1800,
              showConfirmButton: false,
            });
          },
          error: () => {
            swal.fire({
              icon: "error",
              title: "No se pudo eliminar",
              text: "Ocurrió un error inesperado. Intenta de nuevo.",
            });
          },
        });
      });
  }

  /**
   * Regresar va un paso atrás, no directo a la salida: si hay una evaluación
   * abierta, devuelve al listado de monitoreos de este activo, que es de donde
   * se venía; sólo desde el listado sale al Inventario. Antes salía siempre del
   * módulo, y desde el formulario eso era un salto de dos pasos que dejaba al
   * usuario fuera cuando lo único que quería era volver a la lista.
   */
  regresar(): void {
    if (this.formVisible) {
      this.cancelarCaptura();
      return;
    }
    this.router.navigate(["/admin/inventario-ia/gestion"]);
  }

  // ------------------------------------------------------------------
  // Presentación del histórico
  // ------------------------------------------------------------------

  textoDictamen(clave?: string): string {
    if (!clave) {
      return "—";
    }
    const d = MonitoreoInventarioIAComponent.DICTAMENES[clave];
    return d ? d.titulo : clave;
  }

  claseDictamen(clave?: string): string {
    if (!clave) {
      return "is-pendiente";
    }
    const d = MonitoreoInventarioIAComponent.DICTAMENES[clave];
    return d ? d.clase : "is-pendiente";
  }

  textoConclusion(valor?: string | null): string {
    if (!valor) {
      return "—";
    }
    const c = this.conclusiones.find((o) => o.valor === valor);
    return c ? c.etiqueta : valor;
  }

  hallazgos(item: MonitoreoIA): number {
    return (item.aspectos || []).filter((a) => a.estado === "NO_CUMPLE").length;
  }

  // ------------------------------------------------------------------
  // Paginación del histórico (mismo patrón que Gestión de Inventario IA)
  // ------------------------------------------------------------------

  monitoreosPagina(): MonitoreoIA[] {
    const inicio = (this.paginaNorm() - 1) * this.tamPagina;
    return this.monitoreos.slice(inicio, inicio + this.tamPagina);
  }

  totalPaginas(): number {
    const n = this.monitoreos.length;
    return n > 0 ? Math.ceil(n / this.tamPagina) : 1;
  }

  /** Acota la página al rango válido: al borrar la última de la página, se recorre sola. */
  paginaNorm(): number {
    return Math.min(Math.max(this.paginaActual, 1), this.totalPaginas());
  }

  rangoInicio(): number {
    return this.monitoreos.length === 0 ? 0 : (this.paginaNorm() - 1) * this.tamPagina + 1;
  }

  rangoFin(): number {
    return Math.min(this.paginaNorm() * this.tamPagina, this.monitoreos.length);
  }

  irPagina(p: number): void {
    const total = this.totalPaginas();
    if (p < 1 || p > total || p === this.paginaNorm()) {
      return;
    }
    this.paginaActual = p;
    this.cdr.detectChanges();
  }

  primeraPagina(): void { this.irPagina(1); }
  paginaAnterior(): void { this.irPagina(this.paginaNorm() - 1); }
  paginaSiguiente(): void { this.irPagina(this.paginaNorm() + 1); }
  ultimaPagina(): void { this.irPagina(this.totalPaginas()); }

  getPaginationItems(): Array<number | string> {
    const total = this.totalPaginas();
    if (total <= 1) {
      return [1];
    }
    const a = this.paginaNorm();
    const set = new Set<number>([1, total, a - 1, a, a + 1]);
    const ord = Array.from(set).filter((p) => p >= 1 && p <= total).sort((x, y) => x - y);
    const items: Array<number | string> = [];
    for (let k = 0; k < ord.length; k++) {
      const p = ord[k];
      const prev = ord[k - 1];
      if (prev && p - prev > 1) {
        items.push("e" + prev);
      }
      items.push(p);
    }
    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === "number";
  }

  // ------------------------------------------------------------------
  // Utilerías
  // ------------------------------------------------------------------

  /**
   * Date -> texto ISO. Se arma a mano en vez de usar toISOString(), que
   * convierte a UTC y en México adelanta la fecha un día desde las 18:00.
   */
  private dateAISO(fecha?: Date | null): string {
    if (!fecha) {
      return "";
    }
    const y = fecha.getFullYear();
    const m = `${fecha.getMonth() + 1}`.padStart(2, "0");
    const d = `${fecha.getDate()}`.padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  /**
   * Texto ISO -> Date en medianoche LOCAL. Se construye por partes y no con
   * new Date("2026-07-15"), que el navegador interpreta como UTC y en México
   * devolvería el día anterior.
   */
  private isoADate(iso?: string): Date | null {
    if (!iso) {
      return null;
    }
    const [y, m, d] = iso.substring(0, 10).split("-").map(Number);
    return new Date(y, m - 1, d);
  }

  private hoyISO(): string {
    return this.dateAISO(new Date());
  }

  private fechaLegible(valor?: string): string {
    if (!valor) {
      return "—";
    }
    const [y, m, d] = valor.substring(0, 10).split("-");
    return `${d}/${m}/${y}`;
  }

  /** Evita que un folio con HTML se interprete dentro del modal. */
  private escapar(valor?: string): string {
    const div = document.createElement("div");
    div.textContent = valor || "";
    return div.innerHTML;
  }
}
